import { Component, Inject, OnInit } from "@angular/core";
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  CalibrationInformation,
  LoadcellInformationResponse,
  MachineRating,
  SensingElement,
} from "app/api/models";
import { CalibrationService, EmailService } from "app/api/services";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { PrintCertificateEmailDialogComponent } from "../print-certificate-email-dialog/print-certificate-email-dialog.component";
import { AppLoaderService } from "app/core/services/app-loader/app-loader.service";
import { CommonModule, DatePipe } from "@angular/common";
import { FlexLayoutModule } from "@angular/flex-layout";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { DLMSharedModule } from "app/views/calibrations/shared/shared.module";
import { DragScrollModule } from "ngx-drag-scroll";
import { NgxPrintModule } from "ngx-print";
import { PdfComponent } from "app/core/components/pdf/pdf.component";
import autoTable, { RowInput, Styles, UserOptions } from "jspdf-autotable";
import { sortOnProperty } from "app/app.component";

@Component({
  selector: "app-print-certificate-dialog",
  templateUrl: "./print-certificate-dialog.component.html",
  styleUrls: ["./print-certificate-dialog.component.scss", "./../../../../_styles/calibration-overrides.scss"],
  standalone: true,
  imports: [
    CommonModule,
    DLMSharedModule,
    MatListModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    MatMenuModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    FlexLayoutModule,
    DragScrollModule,
    NgxPrintModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatRadioModule,
  ]
})
export class PrintCalibrationCertificateDialogComponent extends PdfComponent implements OnInit {
  Math = Math;
  MachineRating = MachineRating;

  calibration: CalibrationInformation;
  loadcell: LoadcellInformationResponse;

  toggleAlternateCertificate: boolean = false;
  
  public GetCertificateTitle = () => `Calibration-Certificate-${this.loadcell?.loadcell?.loadcellId}.pdf`;

  constructor(
    public dialogRef: MatDialogRef<PrintCalibrationCertificateDialogComponent>,
    private emailService: EmailService,
    private calibrationService: CalibrationService,
    private loader: AppLoaderService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.data != null) {
      this.calibration = this.data.calibration;

      if(this.calibration.resultList?.length)
        this.calibration.resultList = sortOnProperty(this.calibration.resultList, 'machineReadings');
      if(this.calibration.additionalResultList?.length)
        this.calibration.additionalResultList = sortOnProperty(this.calibration.additionalResultList, 'machineReadings');

      //failsafe for machine set but equipment not
      if(this.calibration.machineRating && this.calibration.machineRating != MachineRating.Other && !this.calibration.equipmentList?.length)
        this.calibrationService.apiCalibrationDefaultMachineGet$Json$Response({machineRating: this.calibration.machineRating}).subscribe(res => {
          if(!res?.body?.data) return;
          this.calibration.equipmentList.push({ machine: res.body.data });
        });
      if(this.calibration.additionalMachineRating && this.calibration.additionalMachineRating != MachineRating.Other && !this.calibration.additionalEquipmentList?.length)
        this.calibrationService.apiCalibrationDefaultMachineGet$Json$Response({machineRating: this.calibration.additionalMachineRating}).subscribe(res => {
          if(!res?.body?.data) return;
          this.calibration.additionalEquipmentList.push({ machine: res.body.data });
        });

      this.loadcell = this.data.loadcellInfo;
      this.uploadToBlobStorage();
    }
  }

  getCertificateNumber() {
    const yearNumber = new Date(this.calibration?.date)
      .getFullYear()
      .toString()
      .substr(-2);
    const prefix = `DLM NX 00${yearNumber}`;
    const suffix = this.calibration?.calibrationId.toString().padStart(5, "0");

    return `${prefix} ${suffix}`;
  }

  getMachineRatingText(machineRating: MachineRating) {
    switch (machineRating) {
      case MachineRating.One:
        return "25T";
      case MachineRating.Sixty:
        return "60T";
      case MachineRating.OneHundred:
        return "100T";
      case MachineRating.ThreeThousand:
        return "3000kN";
      case MachineRating.Other:
        return "Other";
      default:
        return "N/A";
    }
  }

  getSensingElementText(sensingElement: SensingElement) {
    switch (sensingElement) {
      case SensingElement.StrainBridge:
        return "Strain Bridge";
      case SensingElement.Mechanical:
        return "Mechanical";
      case SensingElement.PressureGauge:
        return "Pressure Gauge";
      case SensingElement.PressureSensor:
        return "Pressure Sensor";
      default:
        return "N/A";
    }
  }

  getResolutionScale(
    calibration: CalibrationInformation,
    isAdditional: boolean = false
  ) {
    var value = isAdditional
      ? calibration?.additionalResolution ?? 0
      : calibration?.resolution ?? 0;

    // Convert to a string
    const valueAsString = value.toString();

    // Check if there is a decimal point in the string
    if (valueAsString.includes(".")) {
      // The number of decimal places is the length of the substring after the decimal point
      var scale = valueAsString.split(".")[1].length;
      return scale;
    } else {
      return 0;
    }
  }

  getRoundingFactor(scale: number) {
    var factor = Math.pow(10, scale);
    return factor;
  }

  getScaledValue(
    value: number,
    calibration: CalibrationInformation,
    isAdditional: boolean = false
  ) {
    var scale = this.getResolutionScale(calibration, isAdditional);
    var factor = this.getRoundingFactor(scale);
    var result = (Math.round(value * factor) / factor).toFixed(
      scale > 0 ? scale : 0
    );

    return result;
  }

  getAverageError() {
    var results = !this.toggleAlternateCertificate
      ? this.calibration?.resultList
      : this.calibration?.additionalResultList;

    var total = results?.reduce((sum, result) => sum + result?.error, 0) ?? 0;

    return total / this.calibration?.resultList?.length;
  }

  getIsCompressionText() {
    var isCompression = (this.toggleAlternateCertificate ? this.calibration.additionalIsCompression : this.calibration.isCompression);
    return isCompression == null ? "TENSION & COMPRESSION" : isCompression ? "COMPRESSION" : "TENSION";
  }

  private preparePdf(callback: (pdfDoc: jsPDF) => any) {
    let PDF = new jsPDF("p", "mm", "a4", true);

    this.loader.open();


    //title
    this.addTextToPdf(PDF, [[`CERTIFICATE OF CALIBRATION IN ${this.getIsCompressionText()}`]], 12, null, 50, null, true);

    var cellStyle: Partial<Styles> = { lineColor: "#000000", lineWidth: 0.2, cellPadding: {vertical: 1, horizontal: 2}, fontSize: 8, fontStyle: 'normal', };
    var blackCell: Partial<Styles> = { ...cellStyle, textColor: "#FFFFFF", fillColor: "#000000", cellWidth: 32, };
    var whiteCell: Partial<Styles> = { ...cellStyle, textColor: "#000000", fillColor: "#FFFFFF", cellWidth: 64, };

    var blankCell: Partial<Styles> = { ...whiteCell, lineColor: '#FFFFFF', cellWidth: 32, fontSize: 9, cellPadding: {top: 3, bottom: 1, horizontal: 2} };

    var autoTableOptions: UserOptions = {theme: "grid", body: [], tableWidth: 192, margin: {horizontal: 8, top: 50, bottom: 30}, columnStyles: { 0: { cellWidth: 32 }, 1: { cellWidth: 64 }, 2: { cellWidth: 32 }, 3: { cellWidth: 64 }}, };


    //table 1
    autoTable(PDF, {
      ...autoTableOptions,
      startY: ((PDF as any).lastAutoTable.finalY || 15) + 4,
      head: [
          [
            { content: "Customer", styles: blackCell },
            { content: this.loadcell.form10a.thirdPartyCalibrationClient || this.loadcell.loadcell.customerName || '', styles: whiteCell },
            { content: "Certificate Number", styles: blackCell },
            { content: this.getCertificateNumber() ?? '', styles: whiteCell },
          ],
          [
            { content: "Calibrated On", styles: blackCell },
            { content: this.datePipe.transform(this.calibration.date, 'd/MM/YYYY'), styles: whiteCell },
            { content: "Recalibration Due", styles: blackCell },
            { content: this.datePipe.transform(this.calibration.recalibrationDue, 'd/MM/YYYY'), styles: {...whiteCell, textColor: '#ff001a'} }, //font-red
          ],
          [
            { content: "Customer Code", styles: blackCell },
            { content: this.loadcell.customerCode ?? '', styles: whiteCell },
            { content: "Customer Order", styles: blackCell },
            { content: this.loadcell?.form10a?.purchaseOrder ?? '', styles: whiteCell },
          ],
          [
            { content: "DLM Job Number", styles: blackCell },
            { content: this.loadcell.loadcell.loadcellId ?? '', styles: whiteCell },
            { content: "Customer Reference", styles: blackCell },
            { content: this.calibration.customerRef ?? '', styles: whiteCell },
          ],
      ],
    });


    //table 2
    this.addTextToPdf(PDF, [['Loadcell Details']], null, 0, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, 8, true, true);
    
    autoTable(PDF, {
      ...autoTableOptions,
      startY: ((PDF as any).lastAutoTable.finalY || 15) + 1,
      head: [
        [
          { content: "Manufacturer", styles: blackCell },
          { content: this.loadcell.form10a.manufacturer ?? '', styles: whiteCell },
          { content: "Plant Id", styles: blackCell },
          { content: this.loadcell.form10a.plantId ?? '', styles: whiteCell },
        ],
        [
          { content: "Product Type", styles: blackCell },
          { content: this.loadcell.form10a.loadCellType?.name ?? '', styles: whiteCell },
          { content: "Proof Load", styles: blackCell },
          { content: (this.calibration.proofLoad * 100) + '%', styles: whiteCell },
        ],
        [
          { content: "Serial Number", styles: blackCell },
          { content: this.loadcell.form10a.formSerialNumber?.name ?? '', styles: whiteCell },
          { content: "Element Resistance", styles: blackCell },
          { content: this.calibration.resistance ?? '', styles: whiteCell },
        ],
        [
          { content: "Sensing Element", styles: blackCell },
          { content: this.getSensingElementText(this.calibration?.sensingElement) ?? '', styles: whiteCell },
          { content: "Sensitivity at WLL", styles: blackCell },
          { content: this.calibration.output ?? '', styles: whiteCell },
        ],
        [
          { content: "Working Load Limit", styles: blackCell },
          { content: this.calibration.swl ?? '', styles: whiteCell },
          { content: "Average Error", styles: blackCell },
          { content: this.getAverageError().toFixed(2) + '%', styles: whiteCell },
        ],
      ],
    });


    //table 3
    this.addTextToPdf(PDF, [['Test Machine Details']], null, 0, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, 8, true, true);

    var equipmentHead = [
      [
        { content: "Test Machine", styles: blackCell },
        { content: this.getMachineRatingText(!this.toggleAlternateCertificate ? this.calibration?.machineRating : this.calibration.additionalMachineRating) ?? '', styles: whiteCell, colSpan: 3 },
      ],
    ];
    for(var equipment of !this.toggleAlternateCertificate ? this.calibration.equipmentList : this.calibration.additionalEquipmentList) {
      equipmentHead.push(
        [
          { content: "Machine Type", styles: blackCell },
          { content: (equipment.machine?.name ?? '').trim(), styles: whiteCell },
          { content: "Serial Number", styles: blackCell },
          { content: (equipment.machine?.serialNumber ?? '').trim(), styles: whiteCell },
        ],
        [
          { content: "Calibrated to", styles: blackCell },
          { content: equipment.machine?.isoStandard ?? '', styles: whiteCell },
          { content: "Certificate Number", styles: blackCell },
          { content: equipment.machine?.certificateNumber ?? '', styles: whiteCell },
        ]
      );
    }
    autoTable(PDF, {
      ...autoTableOptions,
      startY: ((PDF as any).lastAutoTable.finalY || 15) + 1,
      head: equipmentHead
    });


    //table 4
    if(!this.toggleAlternateCertificate ?
      (this.calibration?.electricalEquipment && !this.calibration.hideElectricalEquipmentOnCert) :
      (this.calibration?.additionalElectricalEquipment && !this.calibration.hideAdditionalElectricalEquipmentOnCert)) {
      this.addTextToPdf(PDF, [['Electrical Equipment Details']], null, 0, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, 8, true, true);
    
      autoTable(PDF, {
        ...autoTableOptions,
        startY: ((PDF as any).lastAutoTable.finalY || 15) + 1,
        head: [
          [
            { content: "Device", styles: {...blackCell, cellWidth: 48 } },
            { content: "Manufacturer", styles: {...blackCell, cellWidth: 48 } },
            { content: "Model Number", styles: {...blackCell, cellWidth: 48 } },
            { content: "Serial Number", styles: {...blackCell, cellWidth: 48 } },
          ],
          [
            { content: (!this.toggleAlternateCertificate ? this.calibration?.electricalEquipment : this.calibration?.additionalElectricalEquipment)?.name ?? '', styles: {...whiteCell, cellWidth: 48 } },
            { content: (!this.toggleAlternateCertificate ? this.calibration?.electricalEquipment : this.calibration?.additionalElectricalEquipment)?.manufacturer ?? '', styles: {...whiteCell, cellWidth: 48 } },
            { content: (!this.toggleAlternateCertificate ? this.calibration?.electricalEquipment : this.calibration?.additionalElectricalEquipment)?.modelNumber ?? '', styles: {...whiteCell, cellWidth: 48 } },
            { content: (!this.toggleAlternateCertificate ? this.calibration?.electricalEquipment : this.calibration?.additionalElectricalEquipment)?.serialNumber ?? '', styles: {...whiteCell, cellWidth: 48 } },
          ]
        ],
      });
    }


    //table 5
    this.addTextToPdf(PDF, [['Certified Readings']], null, 0, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, 8, true, true);
    
    var cols = (
      !this.toggleAlternateCertificate ?
      [this.calibration?.includeRun1, this.calibration.includeRun2, this.calibration.includeRun3, this.calibration.includeRun4, this.calibration.includeRun5] :
      [this.calibration?.additionalIncludeRun1, this.calibration.additionalIncludeRun2, this.calibration.additionalIncludeRun3, this.calibration.additionalIncludeRun4, this.calibration.additionalIncludeRun5]
    )
      .filter(x => !!x).length + 3;
    var cellWidth = 192 / cols;

    var testRunHead: any[][] = [
      [
        { content: "Loadcell Readings" +
              ((!this.toggleAlternateCertificate ? this.calibration?.measurementResponse : this.calibration?.additionalMeasurementResponse)?.measurementDescription ?
                " (" + (!this.toggleAlternateCertificate ? this.calibration?.measurementResponse : this.calibration?.additionalMeasurementResponse)?.measurementDescription + ")"
                : ""),
          styles: {...blackCell, cellWidth: cellWidth, halign: 'center'}, colSpan: cols },
      ],
      [
        { content: "Test Machine" +
                ((!this.toggleAlternateCertificate ? this.calibration?.testMachineMeasurementResponse : this.calibration?.additionalTestMachineMeasurementResponse)?.measurementDescription ?
                  " (" + (!this.toggleAlternateCertificate ? this.calibration?.testMachineMeasurementResponse : this.calibration?.additionalTestMachineMeasurementResponse)?.measurementDescription + ")"
                  : ""),
          styles: {...blackCell, cellWidth: cellWidth}},
        { content: "Desired", styles: {...blackCell, cellWidth: cellWidth} },
      ],
    ];

    //run headers
    for(var i = 1; i <= 5; i++)
      if(this.calibration[(!this.toggleAlternateCertificate ? "includeRun" : "additionalIncludeRun") + i]) {
        testRunHead[1].push(
          { content: "Run " + i, styles: {...blackCell, cellWidth: cellWidth} }
        );
      }

    //error header
    testRunHead[1].push( {content: "Error", styles: {...blackCell, cellWidth: cellWidth}} );

    var resultI = 2;
    for(var result of this.toggleAlternateCertificate ? this.calibration.additionalResultList : this.calibration.resultList) {
      testRunHead.push([]);
      testRunHead[resultI].push(
        { content: this.getScaledValue(result.machineReadings, this.calibration, this.toggleAlternateCertificate), styles: {...whiteCell, cellWidth: cellWidth} },
        { content: this.getScaledValue(result.desired, this.calibration, this.toggleAlternateCertificate), styles: {...whiteCell, cellWidth: cellWidth} },
      );

      for(var i = 1; i <= 5; i++)
        if(this.calibration[(!this.toggleAlternateCertificate ? "includeRun" : "additionalIncludeRun") + i])
          testRunHead[resultI].push(
            { content: this.getScaledValue(result["run" + i], this.calibration, this.toggleAlternateCertificate), styles: {...whiteCell, cellWidth: cellWidth} }
          );
      
      testRunHead[resultI].push(
        { content: this.calibration.includeError ? (result.error ?? 0).toFixed(2) + "%" : "",
          styles: {...whiteCell, cellWidth: cellWidth} }
      );
      
      resultI++;
    }

    autoTable(PDF, {
      ...autoTableOptions,
      startY: ((PDF as any).lastAutoTable.finalY || 15) + 1,
      head: testRunHead,
    });

    this.addTextToPdf(PDF, [['The uncertainties are for a confidence probability of not less than 95%']], 8, null, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, 8, true, true);

    //Signature
    this.addTextToPdf(PDF, [['Certifying Engineer:']], 8, null, ((PDF as any).lastAutoTable.finalY || 15) + 2, null, null, 8, true, true);

    var sigPosition = ((PDF as any).lastAutoTable.finalY || 15) + 1;
    if(sigPosition >= 250) {
      PDF.addPage();
      sigPosition = 55;
    }

    PDF.addImage(this.calibration.signature, 8, sigPosition, 60, 20, null, 'FAST');

    this.addTextToPdf(PDF, [[this.calibration?.employeeName]], 8, null, sigPosition + 15, null, null, 8, true, true);

    // Add header(s) and footer(s)
    this.addHeaderAndFooter(PDF).then(PDF => {
      this.loader.close();
      callback(PDF);
    });
  }
  
  async addHeaderAndFooter(PDF: jsPDF) {
    for(var i=0; i < PDF.getNumberOfPages(); i++) {
      PDF.setPage(i);
      var canvas = await this.createCanvasFromElement("print-header");
      this.addCanvasToPdf(canvas, PDF, 0);
      var canvas2 = await this.createCanvasFromElement("print-footer");
      this.addCanvasToPdf(canvas2, PDF, 272);
    }
    return PDF;
  }

  public openPDF(): void {
    this.preparePdf((PDF) =>
      PDF.save(this.GetCertificateTitle())
    );
  }

  emailCalibrationForm() {
    this.preparePdf((PDF) => {
      var blobPDF = new Blob([PDF.output("blob")], { type: "application/pdf" });

      var dialog = this.dialog.open(PrintCertificateEmailDialogComponent, {
        panelClass: "print-certificate-email-dialog",
        data: {
          calibration: this.data.calibration,
          loadcellInfo: this.data.loadcellInfo,
          blobPDFCertificate: blobPDF,
        },
      });
    });
  }

  printCalibrationForm() {
    this.preparePdf((PDF) => {
      PDF.autoPrint();
      window.open(
        PDF.output("bloburl"),
        "_blank",
        "toolbar=0,location=0,menubar=0"
      );
    });
  }

  uploadToBlobStorage() {
    console.log("attempting to upload to blob storage");
    //check modified date of calibration (or its children) is before last calib file modified - no need to reupload
    this.calibrationService.apiCalibrationIsCertFileStaleGet$Json$Response({calibrationId: this.calibration.calibrationId}).subscribe(res => {
      console.log("currently blob file stale / nonexistant?", res.body.data);
      if(!res?.body?.data) return; //not stale
       
      //otherwise, upload/replace file on blob storage:
      this.preparePdf((PDF) => {
        //upload to azure

        var blob = PDF.output('blob');
        var filename = this.GetCertificateTitle();

        var request = { 
            calibrationId: this.calibration.calibrationId,
            filename: filename,
            body: { formFile: blob },
        };

        this.calibrationService.apiCalibrationUploadCertFileCalibrationIdPost$Json$Response(request).subscribe((res) => {
            if(!res?.body?.data) return;
            console.log("file uploaded - ", res.body.data);
        });

      });
    });
  }
}
